// Client-side micro-batching for community card ratings.
//
// Every mounted rating widget registers its card code here. Codes requested in
// the same tick are fetched with one server call and cached for the session, so
// scrolling a long card grid no longer fires one request per tile.
import { useEffect, useState } from "react";
import { getCardRatings, type CardRatingSummary } from "@/lib/card-ratings-batch.functions";

type Listener = (r: CardRatingSummary) => void;

const cache = new Map<string, CardRatingSummary>();
const listeners = new Map<string, Set<Listener>>();
const pending = new Set<string>();
let timer: ReturnType<typeof setTimeout> | null = null;
let voterToken: string | undefined;

function emit(row: CardRatingSummary) {
  cache.set(row.code, row);
  for (const fn of listeners.get(row.code) ?? []) fn(row);
}

/** Overwrite the cached row after a vote, so every widget for that card agrees. */
export function publishCardRating(row: CardRatingSummary) {
  emit({ ...row, code: row.code.toUpperCase() });
}

async function flush() {
  timer = null;
  const codes = Array.from(pending);
  pending.clear();
  if (!codes.length) return;
  // eBay-style batching: keep each request comfortably under the server cap.
  for (let i = 0; i < codes.length; i += 200) {
    const chunk = codes.slice(i, i + 200);
    try {
      const rows = await getCardRatings({ data: { codes: chunk, ...(voterToken ? { voter: voterToken } : {}) } });
      for (const row of rows) emit(row);
    } catch {
      // Ratings are optional decoration — leave the widgets at their defaults.
    }
  }
}

function request(code: string) {
  pending.add(code);
  if (!timer) timer = setTimeout(() => void flush(), 60);
}

export function useCardRating(code: string, voter?: string): CardRatingSummary {
  const key = code.toUpperCase();
  const [state, setState] = useState<CardRatingSummary>(
    () => cache.get(key) ?? { code: key, votes: 0, average: 0, mine: 0 },
  );

  useEffect(() => {
    if (voter) voterToken = voter;
    const set = listeners.get(key) ?? new Set<Listener>();
    set.add(setState);
    listeners.set(key, set);
    const cached = cache.get(key);
    if (cached) setState(cached);
    else request(key);
    return () => {
      set.delete(setState);
      if (!set.size) listeners.delete(key);
    };
  }, [key, voter]);

  return state;
}
